// Simple analytics helper for non-React contexts
import mixpanel from '../config/mixpanel';

export const captureTrafficSource = () => {
  try {
    const params = new URLSearchParams(window.location.search);

    const utmSource = params.get('utm_source');
    const utmMedium = params.get('utm_medium');
    const utmCampaign = params.get('utm_campaign');
    const utmContent = params.get('utm_content');
    const utmTerm = params.get('utm_term');

    const referrer = document.referrer;
    let referringDomain = '';
    try {
      referringDomain = referrer ? new URL(referrer).hostname : '';
    } catch {}

    const hasUtm = !!utmSource;
    const hasReferrer = !!referringDomain;

    // Only update super properties when there is actual attribution data.
    // Skipping "direct" visits prevents overwriting a previous campaign's
    // super properties every time the user opens the app without UTM params.
    if (hasUtm || hasReferrer) {
      const source = utmSource || referringDomain;
      const medium = utmMedium || (hasReferrer ? 'referral' : undefined);

      const properties: Record<string, string> = { source };
      if (medium) properties.medium = medium;
      if (utmSource) properties.utm_source = utmSource;
      if (utmMedium) properties.utm_medium = utmMedium;
      if (utmCampaign) properties.utm_campaign = utmCampaign;
      if (utmContent) properties.utm_content = utmContent;
      if (utmTerm) properties.utm_term = utmTerm;
      if (referrer) properties.referrer = referrer;
      if (referringDomain) properties.referring_domain = referringDomain;

      // Attach to every subsequent event as super properties (last-touch)
      mixpanel.register(properties);

      // Set first-touch attribution on the user profile (never overwrites)
      mixpanel.people_set_once({
        first_touch_source: source,
        ...(medium && { first_touch_medium: medium }),
        ...(utmSource && { first_utm_source: utmSource }),
        ...(utmMedium && { first_utm_medium: utmMedium }),
        ...(utmCampaign && { first_utm_campaign: utmCampaign }),
        ...(utmContent && { first_utm_content: utmContent }),
        ...(referrer && { first_referrer: referrer }),
        ...(referringDomain && { first_referring_domain: referringDomain }),
      });

      mixpanel.track('attributed_session', {
        source,
        ...(medium && { medium }),
        ...(utmCampaign && { utm_campaign: utmCampaign }),
        ...(utmContent && { utm_content: utmContent }),
        ...(referringDomain && { referring_domain: referringDomain }),
      });
    }
  } catch (error) {
    console.error('Failed to capture traffic source:', error);
  }
};

export const trackEvent = (eventName: string, properties?: Record<string, any>) => {
  try {
    mixpanel.track(eventName, properties);
    console.log('Event tracked:', eventName, properties);
  } catch (error) {
    console.error('Failed to track event:', error);
  }
};

export const identifyUser = (userId: string, userProperties?: Record<string, any>) => {
  try {
    mixpanel.identify(userId);
    if (userProperties) {
      // Set user properties with proper Mixpanel special properties
      const properties = {
        ...userProperties,
        // Ensure email and name are set as Mixpanel special properties
        $email: userProperties.$email || userProperties.email,
        $name: userProperties.$name || userProperties.name,
      };
      mixpanel.people.set(properties);
    }
    console.log('User identified:', userId, userProperties);
  } catch (error) {
    console.error('Failed to identify user:', error);
  }
};

export const resetAnalytics = () => {
  try {
    mixpanel.reset();
    console.log('Analytics reset');
  } catch (error) {
    console.error('Failed to reset analytics:', error);
  }
}; 